import React, { useMemo, useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip, 
	RCCInput,
	RCCAlert
} from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { IndicatorLevel, IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import IndicatorModal from "src/components/ModalIndicator";
import LovSectors from "src/components/Lov/Sectors";
import LovClassifications from "src/components/Lov/Classifications";
import LovUsers from "src/components/Lov/Users";
import { v4 } from "uuid";
import Loader from "src/components/Loader";
import Select from "src/components/Select";
import { Link } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import IndicatorGrouper from "../Hierarchy/components/grouper";
import PresentationGrouper from "./components/PresentationGroup";
import { IIndicatorListFilters, IRefIndicatorGrouper } from "../Hierarchy/interface";
import { IPresentationgroupItem, IRefPresentationModal } from "./interface";
import PresentationGroupModal from "./components/PresentationGroupModal";

const IndicatorsPresentationGroup: React.FC = () => { 
	const { translate } = useTranslation();
	const refModalIndicator = useRef<IRefIndicatorModal>(null);
	const refModalGroupApresentation = useRef<IRefPresentationModal>(null);
	const refStrategic = useRef<IRefIndicatorGrouper>(null);
	const refTatic = useRef<IRefIndicatorGrouper>(null);
	const refOperational = useRef<IRefIndicatorGrouper>(null);

	const [presentationGroups,setPresentationGroups] = useState<Array<IPresentationgroupItem>>([]);
	const groupers = [
		{id:IndicatorLevel.STRATEGIC,ref:refStrategic},
		{id:IndicatorLevel.TATIC,ref:refTatic},
		{id:IndicatorLevel.OPERATIONAL,ref:refOperational}
	];

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [showPresentationFilters, setShowPresentationFilters] = useState(false);
	const [PresentationFilters, setPresentationFilters] = useState({filter:"",active:"T"});
	const [filters, setFilters] = useState<IIndicatorListFilters>({filter:"",situation:"T"});
	
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters,
		control:controlFilters, 
		reset: resetFilters, 
		watch: watchFilters 
	} = useForm();

	const { 
		handleSubmit : handleSubmitPresentationFilters, 
		register : registerPresentationFilters,
		control:controlPresentationFilters, 
		reset: resetPresentationFilters, 
		watch: watchPresentationFilters 
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as IIndicatorListFilters;
			newState.filter = data.filter;
			newState.situation = data.situation.value;

			if(data.classification)
				newState.classification = typeof data.classification === "object" ? data.classification?.value : data.classification;
			if(data.sector)
				newState.sector = typeof data.sector === "object" ? data.sector?.value : data.sector;
			if(data.responsible)
				newState.responsible = typeof data.responsible === "object" ? data.responsible?.value : data.responsible;

			return newState;
		});
	};

	const areUsingFilter = ():boolean => !!filters.classification || !!filters.filter || !!filters.responsible || !!filters.sector || filters.situation !== "T";

	const submitPresentationFilters = (data: any) => {
		setPresentationFilters({filter:data.filter,active:data.active.value});
	};

	const { errorFetching, loading, loadData } = useEffectFetchData<IPresentationgroupItem[]> (
		"PresentationGroups/List", 
		{ 
			...PresentationFilters
		 },
		 ({ data }) => {
			setPresentationGroups(data);
		 },
		 [PresentationFilters]
	);


	const getSelectedIndicators = () => {

		const taticList = refTatic.current?.getSelectedIndicators()||[];
		const operationalList = refOperational.current?.getSelectedIndicators()||[];
		const strategicList = refStrategic.current?.getSelectedIndicators()||[];

		const selectedList = [...taticList,...operationalList,...strategicList];
		
		return selectedList;
	};

	const cleanSelectedIndicators = () => {

		refTatic.current?.cleanSelectedIndicators(false);
		refOperational.current?.cleanSelectedIndicators(false);
		refStrategic.current?.cleanSelectedIndicators(false);

	};

	const removeGroup = (group:IPresentationgroupItem) => {

		refModalGroupApresentation.current?.actionButtonDelete(group,() => {
			setPresentationGroups((prevState) => 
				prevState.filter((x) => x.iqa001Id !== group.iqa001Id)
			);
		});
	};

	const updateGroup = (group:IPresentationgroupItem) => {

		refModalGroupApresentation.current?.actionButtonChange(group,() => {
			loadData();
		});
	};

	const forceGrouperRequery = (newIndicator :any) => {
		const grouper = groupers.find((x) => x.id === newIndicator.indicator.iqc001Nivel);
		if(grouper)
			grouper.ref.current?.forceRequery();
	};

	const renderGroupers = () => 
		<>
			{groupers.map((grouper) => 
				<IndicatorGrouper 
					key={`${grouper.id}-grouper-indicators`}
					filters={filters}
					level={grouper.id}
					disabled={false}
					requestMethod="Indicators/List"
					ref={grouper.ref}
					filterHierarchy
				/>
			)}
		</>
	;

	const renderPresentationGroups = useMemo(() => 
		<>
			{presentationGroups.length > 0 ? 
				presentationGroups.map((group) => (
					<PresentationGrouper
						key={v4()}
						group={group}
						getSelectedList = {getSelectedIndicators}
						removeGroupFunction = {removeGroup}
						updateGroupFunction = {updateGroup}
						insertIndicatorCallback = {cleanSelectedIndicators}
					/>
				))
				:
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Não foi encontrado grupos de apresentação")}
					color="blue"
				/>
			}
		</>
	,[presentationGroups]);

	return (
		<RCCGrid container item
			direction="row-reverse"
			spacing={5}
		>
			<RCCGrid container item
				direction="column"
				sm={12}
				md={6}
			>
				<PageTitle
					title={translate("Indicadores")}
					icon={() => <AppIcons name="indicators" />}
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || areUsingFilter() ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || areUsingFilter() ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Novo indicador")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => { refModalIndicator?.current?.actionButtonAdd((newIndicator) => forceGrouperRequery(newIndicator));}}
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
				</PageTitle>
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						filter: "",
						sector:{value:"",label:""},
						responsible:{value:"",label:""},
						classification:{value:"",label:""},
						situation:{value: "T", label: translate("Todos")}
					})}
					showFilters={showFilters}
				>	
					<RCCGrid container item>
						<RCCGrid container item
							direction="row"
							spacing={1}
						>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<LovSectors
									control={controlFilters}
									name="sector"
									label={translate("Setor")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<LovClassifications
									control={controlFilters}
									name="classification"
									label={translate("Classificação")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
							>
								<LovUsers
									control={controlFilters}
									name="responsible"
									label={translate("Responsável")}
									isClearable
								/>
							</RCCGrid>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<Select 
									control={controlFilters}
									name="situation" 
									options={[
										{value: "A", label: translate("Ativo")}, 
										{value: "I", label: translate("Inativo")},
										{value: "T", label: translate("Todos")}
									]} 
									label={translate("Situação")}
									defaultValue={{value: "T", label: translate("Todos")}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
				<RCCGrid container
					styleInline="margin-top:0.5rem;">
					{renderGroupers()}
				</RCCGrid>
			</RCCGrid>
			<RCCGrid container item
				direction="column"
				sm={12}
				md={6}
			>
				<PageTitle
					title={translate("Grupos de apresentação")}
					icon={{
						name: "hierarchy"
					}}
				>
					<RCCTooltip
						text={translate("Apresentação")}
						color="grey"
					>
						<Link to="/indicators/presentation">	
							<RCCButton 
								color="grey"
								icon = {{ name: "eye" }}
								layout="isRounded"
								size="md"
							/>
						</Link>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showPresentationFilters || PresentationFilters.filter || PresentationFilters.active !== "T"? "blue" : "grey"}
					>
						<RCCButton 
							color={showPresentationFilters || PresentationFilters.filter  || PresentationFilters.active !== "T" ? "blue" : "grey"}
							onClick = {() => {setShowPresentationFilters((prevState) => !prevState);}}
							icon = {{name: "filter"}}
							layout="isRounded"
							size="md"
						/>
					</RCCTooltip>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Novo grupo")}
							color="purple"
						>
							<RCCButton 
								onClick = {() => {refModalGroupApresentation.current?.actionButtonAdd(() => loadData());}}
								icon = {{
									name: "plus"
								}}
								layout="isRounded"
								color="purple"
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
				</PageTitle>
				<FiltersContainer
					onSubmitFilters={handleSubmitPresentationFilters(submitPresentationFilters)}
					resetFilters={() => resetPresentationFilters({ 
						filter: "",
						active:{value: "T", label: translate("Todos")}
					})}
					showFilters={showPresentationFilters}
				>	
					<RCCGrid container item>
						<RCCGrid container item
							direction="row"
							spacing={1}
						>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<Select 
									control={controlPresentationFilters}
									name="active" 
									options={[
										{value: "S", label: translate("Sim")}, 
										{value: "N", label: translate("Não")},
										{value: "T", label: translate("Todos")}
									]} 
									label={translate("Ativos")}
									defaultValue={{value: "T", label: translate("Todos")}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerPresentationFilters}
									defaultValue={watchPresentationFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
				{errorFetching ? (
					<RCCAlert 
						color="red"
						title={translate("Atenção")}
						description={translate("Erro ao carregar dados")}
					/>
				): 
					<>
						{loading ? <Loader /> : renderPresentationGroups}
					</>
				}
			</RCCGrid>
			<IndicatorModal ref={refModalIndicator} /> 
			<PresentationGroupModal ref={refModalGroupApresentation} />
		</RCCGrid>
	);
};

export default IndicatorsPresentationGroup;
